import React from 'react';
import {
  ClockCircleOutlined,
  DotChartOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  UsergroupDeleteOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { CardDataStateKind } from 'containers/Intelligence/types';

/** Every kind that shows a marker: `ok` has a body, and `loading` shows the skeleton instead. */
export type MarkerKind = Exclude<CardDataStateKind, 'ok' | 'loading'>;

// Held here rather than in `CardDataStateBlock` because the tile grain draws the same pairing from
// the same state (`MetricValue`), and two copies would let one card and one tile name the same
// failure differently. PLA-8828.
export const MARKER_LABEL_KEYS: Record<MarkerKind, string> = {
  technicalError: 'TechnicalError',
  insufficientData: 'InsufficientData',
  notConfigured: 'NotConfigured',
  awaitingData: 'AwaitingData',
  permissionRestricted: 'PermissionRestricted',
  consentRestricted: 'ConsentRestricted',
  noMatchingCustomers: 'NoMatchingCustomers'
};

/**
 * The reasons under `insufficientData` that mean the reading DOES NOT APPLY: `assembleCard` gives
 * that kind to every metric that answered without a figure, so a card was headed "Insufficient Data"
 * directly above "This reading does not apply to this tenant's industry". Only these two, because
 * the workbook's payload contract says of `reason_code` and `calculation_status` "Do not infer
 * complete enum here" — the readiness guardrail codes are its SUPPRESSED state, a third thing again.
 * PLA-8828.
 */
const NOT_APPLICABLE_REASONS = new Set(['industry_out_of_scope', 'not_applicable']);

/** The `DataStates` label a marker draws: its kind's, unless the reason renames it. */
export const markerLabelKey = (kind: MarkerKind, reason?: string): string =>
  kind === 'insufficientData' && NOT_APPLICABLE_REASONS.has((reason ?? '').trim().toLowerCase())
    ? 'NotApplicable'
    : MARKER_LABEL_KEYS[kind];

export const MARKER_ICONS: Record<MarkerKind, React.ComponentType> = {
  technicalError: ExclamationCircleOutlined,
  insufficientData: DotChartOutlined,
  notConfigured: SettingOutlined,
  awaitingData: ClockCircleOutlined,
  permissionRestricted: LockOutlined,
  consentRestricted: SafetyCertificateOutlined,
  noMatchingCustomers: UsergroupDeleteOutlined
};

/** Only a failed request is drawn in the negative hue; every other marker is a designed state. */
export const markerTone = (kind: MarkerKind): 'negative' | 'accent' =>
  kind === 'technicalError' ? 'negative' : 'accent';

// The one icon+label pairing every data-state marker uses, at the card grain (`CardDataStateBlock`)
// and at the tile grain (`MetricValue`'s `notConfigured` reading) alike — extracted so the two can
// never visually drift apart the way they did once already. PLA-8815.
export const DataStateIcon = styled.span`
  display: inline-flex;
  color: ${tokens.color['accent-blue']};
  font-size: 19px;
  line-height: 19px;

  &[data-tone='negative'] {
    color: ${tokens.color.negative};
  }
`;

// A `span`, not a `p`: a flex/inline-flex parent blockifies it regardless, so the card-level block
// layout is unaffected, and the tile-level inline layout does not nest a paragraph inside a span.
export const DataStateLabel = styled.span`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['card-title-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-title-line']};
`;
